import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Typography, IconButton, Paper, Button } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Close';
import ActionType from 'src/enums/actionType';
import utils from 'src/utils/utils';
import apiClient from 'src/utils/apiClient';
import { useLoader } from 'src/reducers/loader';
import {
  useCheckContentFieldsAndSelectedColumns,
  useDeleteCmpAction,
  useResetContentEditorFlowData,
} from 'src/components/massAction/state/massAction';
import CustCard from 'src/components/common/ActionCard';
import { CONTENT } from '../MassActionEditor';
import { COMMERCIAL_RELAUNCH, CONTENT_EDITOR } from './MassActionMessage';
import { makeStyles } from 'tss-react/mui';
import { useSelectCommercialRelaunchContent } from 'src/components/massAction/state/commercialMassActionEditorActions';
import useApiCaller from 'src/hooks/useApiCaller';

const useStyles = makeStyles()(theme => ({
  deleteBtn: {
    margin: '2px',
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.primary.contrastText,
    '&:hover': {
      backgroundColor: theme.palette.warning.main,
      opacity: 0.9,
    },
  },
}));

const ActionEditor = ({ action, actionTypeId, isWritable, onEditMessage, onDelete, lang }) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const deleteCampaignAction = useDeleteCmpAction();

  return (
    <>
      <CustCard style={{ overflow: 'hidden' }} title={t(lang.title[actionTypeId])}>
        <Paper
          style={{ height: '200px', width: '160px', display: 'flex', flexDirection: 'column' }}
        >
          <Typography
            color="primary"
            style={{
              wordWrap: 'normal',
              padding: '8px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {t(lang.createText[actionTypeId])}
          </Typography>
          <div style={{ flex: 1 }}>
            {isWritable && (
              <IconButton
                onClick={event => {
                  event.preventDefault();
                  deleteCampaignAction(action.id, onDelete);
                }}
                className={classes.deleteBtn}
                size="small"
              >
                <DeleteIcon />
              </IconButton>
            )}
          </div>
          <div style={{ padding: '12px', display: 'grid' }}>
            <Button
              variant="contained"
              color="primary"
              onClick={event => {
                event.preventDefault();
                onEditMessage();
              }}
            >
              {t('smartmessaging.contentEditor.useContent')}
            </Button>
          </div>
        </Paper>
      </CustCard>
    </>
  );
};

ActionEditor.propTypes = {
  action: PropTypes.object.isRequired,
  actionTypeId: PropTypes.number.isRequired,
  isWritable: PropTypes.bool.isRequired,
  onEditMessage: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  lang: PropTypes.object.isRequired,
};

function ActionEditorHandler(props) {
  const callApi = useApiCaller();
  const checkContentFieldsAndSelectedColumns = useCheckContentFieldsAndSelectedColumns();
  const resetContentEditorFlowData = useResetContentEditorFlowData();
  const selectCommercialRelaunchContent = useSelectCommercialRelaunchContent();
  const setLoader = useLoader();
  async function onEditMessage() {
    resetContentEditorFlowData();
    setLoader(true);

    const message = await callApi(apiClient.getMessage, [props.action.messageId]).catch(() =>
      props.setLoader(false)
    );

    if (props.actionTypeId === ActionType.PROSPECTING.id) {
      setLoader(false);
      await selectCommercialRelaunchContent(props.action, message, props.actionTypeId);
      props.onEdit();
      props.goToStep(CONTENT, {
        actionId: props.action.id,
        actionType: props.actionTypeId,
        initialStep: COMMERCIAL_RELAUNCH,
      });
      return;
    }
    const contentStoredFileId =
      props.actionTypeId === ActionType.XPLORACTIVE_NEWS.id
        ? JSON.parse(message.xplorNewsContent).contentStoredFileId
        : message.contentStoredFileId;

    let content = '';
    if (contentStoredFileId) {
      content = await callApi(apiClient.get, [utils.link(contentStoredFileId)]).finally(() =>
        setLoader(false)
      );
    }
    const { object } = message;

    checkContentFieldsAndSelectedColumns(
      [ActionType.SMS.id, ActionType.XPLORACTIVE_NEWS.id].includes(props.actionTypeId)
        ? content
        : [object, content],
      props.actionTypeId,
      processedFields => {
        props.onEdit();
        props.goToStep(CONTENT, {
          actionType: props.actionTypeId,
          actionId: props.action.id,
          initialStep: CONTENT_EDITOR,
          initialExtraProps: {
            fields: processedFields,
            content,
            object: object ?? '',
            initialMessage: message,
            initialAction: props.action,
          },
        });
      }
    );
  }

  const [testFormIsVisible, showTestForm] = useState(false);

  return <ActionEditor {...props} {...{ onEditMessage, testFormIsVisible, showTestForm }} />;
}

ActionEditorHandler.propTypes = {
  actionTypeId: PropTypes.number.isRequired,
  isWritable: PropTypes.bool.isRequired,
  onDelete: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  lang: PropTypes.object.isRequired,
  goToStep: PropTypes.func.isRequired,
  action: PropTypes.object.isRequired,
};

export default ActionEditorHandler;
